import React from "react";
import { MathJax } from "better-react-mathjax";

export function IterativeSolversIntro({ lite = false }) {
  const baseClass = lite
    ? 'space-y-2 text-justify hyphens-auto text-sm leading-relaxed text-white/80 md:text-base'
    : 'space-y-4 text-justify hyphens-auto leading-relaxed text-white/85 text-sm md:text-base';
  return (
    <div className={baseClass}>
      Iterative linear solvers approach the solution of <MathJax inline>{"\\( Ax=b \\)"}</MathJax> by
      iteratively minimizing the residual of the linear system. Direct factorization methods (LU/Cholesky) also work for sparse
      matrices, but for <em>very large</em> problems the <em>fill-in</em> and memory/time costs can be very high.
      Iterative solvers avoid explicit inversion and instead minimize the residual <MathJax inline>{"\\( r=b-Ax \\)"}</MathJax> using
      the sparse structure, diagonal dominance and symmetry to perform inexpensive updates. Stationary methods
      (Richardson, Jacobi, forward/backward/symmetric Gauss–Seidel, SSOR) are lightweight and easy to implement; they can
      also act as preconditioners. Krylov subspace methods (Conjugate Gradient, GMRES) achieve fast convergence by
      combining information from successive residuals; they’re the default choice for large, sparse systems.
    </div>
  );
}

export default function IterativeLinearSolversLongExplanation() {
  return (
    <div className="space-y-6 text-sm leading-relaxed text-white/80 text-justify hyphens-auto md:text-base">
      <h2 className="text-sm font-semibold md:text-base">1. Problem Setting and Fixed-Point Form</h2>
      <p>
        We seek <MathJax inline>{"\\( x\\in\\mathbb{R}^n \\)"}</MathJax> with <MathJax inline>{"\\( Ax=b \\)"}</MathJax>, where <MathJax inline>{"\\( A \\)"}</MathJax> is typically
        large and sparse. Iterative methods start from <MathJax inline>{"\\( x^{(0)} \\)"}</MathJax> and
        generate a sequence <MathJax inline>{"\\( x^{(k)} \\rightarrow x^* \\)"}</MathJax>. A common viewpoint writes a splitting <MathJax inline>{"\\( A=M-N \\)"}</MathJax>, where
        the matrix <MathJax inline>{"\\( M \\)"}</MathJax> is chosen so that solving <MathJax inline>{"\\( Mz=y \\)"}</MathJax> is cheap. The iteration is
      </p>
      <MathJax>{String.raw`\[
      \begin{aligned}
        x^{(k+1)} &= M^{-1}(Nx^{(k)} + b) \\
        &=\ x^{(k)} + M^{-1}(b - Ax^{(k)}) .
        \end{aligned}
      \]`}</MathJax>
      <p>
        <strong>Implementation detail:</strong> the formula uses <MathJax inline>{"\\( M^{-1} \\)"}</MathJax> symbolically, but
        we <em>do not invert</em> <MathJax inline>{"\\( M \\)"}</MathJax>. Each step solves the local
        linear system <MathJax inline>{"\\( M x^{(k+1)} = N x^{(k)} + b \\)"}</MathJax> (e.g., by forward/backward
        substitution when <MathJax inline>{"\\( M \\)"}</MathJax> is triangular).
      </p>
      <p>
        Let <MathJax inline>{"\\( r^{(k)} := b-Ax^{(k)} \\)"}</MathJax> and <MathJax inline>{"\\( e^{(k)} := x^*-x^{(k)} \\)"}</MathJax>.
        Then <MathJax inline>{"\\( e^{(k+1)} = Ge^{(k)} \\)"}</MathJax> with iteration matrix <MathJax inline>{"\\( G := M^{-1}N \\)"}</MathJax>. Convergence is governed by
        the spectral radius <MathJax inline>{"\\( \\rho(G)<1 \\)"}</MathJax>.
      </p>
      <p>
        <strong>How do we choose <MathJax inline>{"\\( M \\)"}</MathJax> and <MathJax inline>{"\\( N \\)"}</MathJax>? </strong>
        We pick <MathJax inline>{"\\( M \\)"}</MathJax> to be an approximation to <MathJax inline>{"\\( A \\)"}</MathJax> that’s
        cheap to solve with, and set <MathJax inline>{"\\( N=M-A \\)"}</MathJax>.
        Split <MathJax inline>{"\\( A=D-L-U \\)"}</MathJax> with diagonal <MathJax inline>{"\\( D \\)"}</MathJax>, then some ommon choices are
      </p>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Jacobi:</strong> <MathJax inline>{"\\( M=D \\)"}</MathJax> (diagonal of <MathJax inline>{"\\( A \\)"}</MathJax>), <MathJax inline>{"\\( N=D-A=L+U \\)"}</MathJax>.</li>
        <li><strong>Gauss–Seidel (forward):</strong> <MathJax inline>{"\\( M=D-L \\)"}</MathJax>, <MathJax inline>{"\\( N=U \\)"}</MathJax>.</li>
        <li><strong>Gauss–Seidel (backward):</strong> <MathJax inline>{"\\( M=D-U \\)"}</MathJax>, <MathJax inline>{"\\( N=L \\)"}</MathJax>.</li>
        <li><strong>SSOR (with <MathJax inline>{"\\( \\omega \\)"}</MathJax>):</strong> uses a forward SOR step followed by a backward SOR step (see §2.4), yielding a symmetric effective <MathJax inline>{"\\( M \\)"}</MathJax>.</li>
        <li><strong>Richardson (with step <MathJax inline>{"\\( \\alpha \\)"}</MathJax>):</strong> can be seen as <MathJax inline>{"\\( M=\\tfrac{1}{\\alpha}I \\)"}</MathJax>, <MathJax inline>{"\\( N=M-A \\)"}</MathJax>, giving the residual-correction form.</li>
      </ul>

      <h2 className="text-sm font-semibold md:text-base">2. Stationary methods</h2>

      <h3 className="font-semibold mt-2">2.1 Richardson (Damped Residual Correction)</h3>
      <p>
        The simplest update uses a scalar relaxation <MathJax inline>{"\\( \\alpha>0 \\)"}</MathJax>:
      </p>
      <MathJax>{String.raw`\[
      \begin{aligned}
        x^{(k+1)} &= x^{(k)} + \alpha\, (b-Ax^{(k)}),\\
         G &= I-\alpha A .
      \end{aligned}
      \]`}</MathJax>
      <ul className="list-disc pl-6 space-y-1">
        <li><em>Convergence:</em> requires <MathJax inline>{"\\( \\rho(I-\\alpha A)<1 \\)"}</MathJax>. For SPD <MathJax inline>{"\\( A \\)"}</MathJax>, the optimal constant
          step is <MathJax inline>{"\\( \\alpha_* = \\tfrac{2}{\\lambda_{\\min}+\\lambda_{\\max}} \\)"}</MathJax>, yielding
          a rate <MathJax inline>{"\\( \\tfrac{\\kappa-1}{\\kappa+1} \\)"}</MathJax> with condition
          number <MathJax inline>{"\\( \\kappa=\\lambda_{\\max}/\\lambda_{\\min} \\)"}</MathJax>.
        </li>
        <li><em>Use:</em> baseline smoother and a simple preconditioner when used inside Krylov methods.</li>
      </ul>

      <h3 className="font-semibold mt-2">2.2 Jacobi</h3>
      <p>
        Split <MathJax inline>{"\\( A=D-L-U \\)"}</MathJax> with diagonal <MathJax inline>{"\\( D \\)"}</MathJax>. The iteration is
      </p>
      <MathJax>{String.raw`\[
      \begin{aligned}
        x^{(k+1)} &= D^{-1}\bigl((L+U)x^{(k)} + b\bigr), \\
        G &= D^{-1}(L+U).
        \end{aligned}
      \]`}</MathJax>
      <ul className="list-disc pl-6 space-y-1">
        <li><em>Convergence:</em> guaranteed for strictly diagonally dominant or SPD matrices with suitable ordering.</li>
        <li><em>Variant:</em> damped/weighted Jacobi with <MathJax inline>{"\\( \\omega\\in(0,2) \\)"}</MathJax>: 
         <MathJax>{String.raw`\[
         \begin{aligned}
        x^{(k+1)} &= (1-\omega)x^{(k)} \\
        &+ \omega D^{-1}(b-(L+U)x^{(k)}).
        \end{aligned}
      \]`}</MathJax>
        </li>
      </ul>

      <h3 className="font-semibold mt-2">2.3 Gauss–Seidel (Forward, Backward, Symmetric)</h3>
      <p>
        Gauss–Seidel (GS) improves Jacobi by using newly computed components immediately during a step.
        With <MathJax inline>{"\\( A = D - L - U \\)"}</MathJax> (strictly lower <MathJax inline>{"\\( L \\)"}</MathJax>, strictly upper <MathJax inline>{"\\( U \\)"}</MathJax>):
      </p>
      <p><strong>Forward GS (FGS):</strong> uses <MathJax inline>{"\\( M = D-L \\)"}</MathJax>, <MathJax inline>{"\\( N=U \\)"}</MathJax> and solves the lower-triangular system</p>
      <MathJax>{String.raw`\[
        (D-L)\,x^{(k+1)} = b + U\,x^{(k)}.
      \]`}</MathJax>
      <p>
        Components are updated in increasing index order. Each <MathJax inline>{"\\( x_i^{(k+1)} \\)"}</MathJax> uses
        the newest <MathJax inline>{"\\( x_1^{(k+1)},\\dots,x_{i-1}^{(k+1)} \\)"}</MathJax> and the old <MathJax inline>{"\\( x_{i+1}^{(k)},\\dots x_{n}^{(k)}\\)"}</MathJax>.
      </p>
      <p><strong>Backward GS (BGS):</strong> uses <MathJax inline>{"\\( M = D-U \\)"}</MathJax>, <MathJax inline>{"\\( N=L \\)"}</MathJax> and solves</p>
      <MathJax>{String.raw`\[
        (D-U)\,x^{(k+1)} = b + L\,x^{(k)},
      \]`}</MathJax>
      <p>updating in decreasing index order. This can be advantageous when data dependencies or boundary conditions align with a reverse step.</p>
      <p><strong>Symmetric GS (SGS):</strong> performs one forward step followed by one backward step each iteration:</p>
      <MathJax>{String.raw`\[
      \begin{aligned}
        (D-L) x^{(k+\tfrac12)} &= b + U x^{(k)}, \\
        (D-U) x^{(k+1)} &= b + L x^{(k+\tfrac12)} .
      \end{aligned}
      \]`}</MathJax>
      <ul className="list-disc pl-6 space-y-1">
        <li><em>Convergence:</em> GS converges for SPD and strictly diagonally dominant systems; SGS preserves symmetry and is often preferred as a CG/SSOR preconditioner.</li>
        <li><em>Ordering:</em> reordering rows/columns can change speed; for PDE matrices, natural grid orderings often matter.</li>
        <li><em>Implementation:</em> GS needs one sparse step with triangular solves which is simple and memory-friendly.</li>
      </ul>

      <h3 className="font-semibold mt-2">2.4 SSOR (Symmetric Successive Over-Relaxation)</h3>
      <p>
        SSOR is the symmetric, over-relaxed counterpart of GS with parameter
        <MathJax inline>{"\\( \\omega\\in(0,2) \\)"}</MathJax>. One SSOR step is a forward SOR step followed by a backward SOR step:
      </p>
      <MathJax>{String.raw`\[
      \begin{aligned}
        (D-\omega L) x^{(k+\tfrac12)} &= \bigl[(1-\omega)D+\omega U\bigr]x^{(k)}\\
        &+ \omega b,
        \end{aligned}
      \]`}</MathJax>
      <MathJax>{String.raw`\[
      \begin{aligned}
        (D-\omega U) x^{(k+1)} &=\bigl[(1-\omega)D+\omega L\bigr]x^{(k+\tfrac12)}\\
         &+\omega b.
        \end{aligned}
      \]`}</MathJax>
      <ul className="list-disc pl-6 space-y-1">
        <li><em>Convergence:</em> for SPD, <MathJax inline>{"\\( 0<\\omega<2 \\)"}</MathJax>. The “best” <MathJax inline>{"\\( \\omega \\)"}</MathJax> is problem-dependent
          (model Poisson problems admit near-optimal formulas); in practice try a few values in <MathJax inline>{"\\( [1,1.9] \\)"}</MathJax>.
        </li>
        <li><em>As preconditioner:</em> SSOR is frequently used as <MathJax inline>{"\\( M \\)"}</MathJax> for CG/GMRES because it is symmetric and cheap.</li>
      </ul>

      <h2 className="text-sm font-semibold md:text-base">3. Krylov Subspace Methods</h2>
      <p>
        Define the Krylov space  <MathJax inline>{"\\( \\mathcal{K}_k(A,r^{(0)})\\} \\)"}</MathJax> as <MathJax inline>{"\\( \\mathcal{K}_k(A,r^{(0)})=\\mathrm{span}\\{r^{(0)},Ar^{(0)},\\dots,A^{k-1}r^{(0)}\\} \\)"}</MathJax>.
        Krylov methods pick <MathJax inline>{"\\( x^{(k)} \\in x^{(0)}+\\mathcal{K}_k \\)"}</MathJax> to optimize a tractable criterion.
        They use information from successive residuals to accelerate convergence—this is why we introduce them here,
        and why Conjugate Gradient (CG) naturally appears as a member of this class for SPD systems.
      </p>

      <h3 className="font-semibold mt-2">3.1 Conjugate Gradient (CG)</h3>
      <ul className="list-disc pl-6 space-y-1">
        <li><em>Applicability:</em> symmetric positive definite (SPD) <MathJax inline>{"\\( A \\)"}</MathJax>.</li>

        <li><em>Characterization:</em> <MathJax inline>{"\\( x^{(k)} \\)"}</MathJax> minimizes the quadratic energy
          <MathJax inline>{"\\( \\tfrac12 x^\\top A x - b^\\top x \\)"}</MathJax> over
          <MathJax inline>{"\\( x^{(0)} + \\mathcal{K}_k(A,r^{(0)}) \\)"}</MathJax>.
          Search directions are <em>A-conjugate</em> (mutually orthogonal in the <MathJax inline>{"\\( A \\)"}</MathJax>-inner product).
        </li>

        <li><em>Iteration (standard form):</em></li>
      </ul>

      <MathJax>{String.raw`\[
\begin{aligned}
& r_0 = b - A x_0,\quad p_0 = r_0,\\[4pt]
& \text{for } k = 0,1,2,\dots:\\[3pt]
&\quad \alpha_k = \frac{r_k^\top r_k}{p_k^\top A p_k},\\[3pt]
&\quad x_{k+1} = x_k + \alpha_k\,p_k,\\[3pt]
&\quad r_{k+1} = r_k - \alpha_k\,A p_k,\\[3pt]
&\quad \beta_k = \frac{r_{k+1}^\top r_{k+1}}{r_k^\top r_k},\\[3pt]
&\quad p_{k+1} = r_{k+1} + \beta_k\,p_k.
\end{aligned}
\]`}</MathJax>

      <ul className="list-disc pl-6 space-y-1">
        <li><em>Error bound:</em> with condition number <MathJax inline>{"\\( \\kappa(A) \\)"}</MathJax>,
          <MathJax>{String.raw`\[
          \begin{aligned}
      \|x^{(k)}-x^*\|_A \le 2&\!\left(\frac{\sqrt{\kappa}-1}{\sqrt{\kappa}+1}\right)^k\\
      &\|x^{(0)}-x^*\|_A.
      \end{aligned}
    \]`}</MathJax>
          A good preconditioner reduces <MathJax inline>{"\\( \\kappa \\)"}</MathJax> and thus the iteration count.
        </li>

        <li><em>Cost per iteration:</em> one sparse matrix–vector product, a few vector
          updates, and two inner products (often combined to reduce synchronization).
        </li>
      </ul>


      <h3 className="font-semibold mt-2">3.2 GMRES</h3>
      <ul className="list-disc pl-6 space-y-1">
        <li><em>Applicability:</em> general (possibly nonsymmetric) <MathJax inline>{"\\( A \\)"}</MathJax>.</li>

        <li><em>Characterization:</em> <MathJax inline>{"\\( x^{(k)} \\)"}</MathJax> minimizes the residual norm
          <MathJax inline>{"\\( \\|b-Ax\\|_2 \\)"}</MathJax> over
          <MathJax inline>{"\\( x^{(0)} + \\mathcal{K}_k(A, r^{(0)}) \\)"}</MathJax>
          using an orthonormal basis of the Krylov subspace produced by the Arnoldi process.
        </li>

        <li><em>Iteration (Arnoldi-based GMRES algorithm):</em></li>
      </ul>

      <MathJax>{String.raw`\[
\begin{aligned}
& r_0 = b - A x_0,\quad \beta = \|r_0\|_2,\quad v_1 = r_0 / \beta,\\[4pt]
& \text{for } j = 1,2,\dots,k:\\[3pt]
&\quad w = A v_j,\\[3pt]
&\quad \text{for } i = 1,\dots,j:\\[3pt]
&\quad\quad  h_{i,j} = v_i^\top w,\quad w = w - h_{i,j} v_i,\\[3pt]
&\quad \text{end for}\\[3pt]
&\quad h_{j+1,j} = \|w\|_2,\quad v_{j+1} = w / h_{j+1,j},\\[3pt]
& \text{end for.}
\end{aligned}
\]`}</MathJax>

      <p className="pl-6">
        After <MathJax inline>{"\\( k \\)"}</MathJax> Arnoldi steps, we have the
        relation <MathJax inline>{"\\( A V_k = V_{k+1} \\underline{H}_k \\)"}</MathJax>,
        where <MathJax inline>{"\\( V_k=[v_1,\\dots,v_k] \\)"}</MathJax> has orthonormal columns and <MathJax inline>{"\\( \\underline{H}_k \\in \\mathbb{R}^{(k+1)\\times k} \\)"}</MathJax> is
        upper Hessenberg.
        The next iterate is found by solving a small least-squares problem:
      </p>
      <MathJax>{String.raw`\[
      \begin{aligned}
y_k &= \arg\min_{y\in\mathbb{R}^k} \|\beta e_1 - \underline{H}_k y\|_2,\\
x_k &= x_0 + V_k y_k.
\end{aligned}
\]`}</MathJax>

      <ul className="list-disc pl-6 space-y-1">
        <li><em>Storage and work:</em> each step stores one new basis vector and
          requires <MathJax inline>{"\\( j \\)"}</MathJax> inner products for orthogonalization.
          The total cost and memory grow as <MathJax inline>{"\\( \\mathcal{O}(kn) \\)"}</MathJax>.
        </li>
        <li><em>Restarted GMRES(<MathJax inline>{"\\( m \\)"}</MathJax>):</em> to control memory, GMRES is often
          restarted after <MathJax inline>{"\\( m \\)"}</MathJax> steps by
          setting <MathJax inline>{"\\( x_0 \\leftarrow x_m \\)"}</MathJax> and repeating, trading some convergence speed for lower cost.
        </li>
        <li><em>Convergence:</em> residuals decrease monotonically: <MathJax inline>{"\\( \\|r_{k+1}\\|_2 \\le \\|r_k\\|_2 \\)"}</MathJax>.
          The rate depends on how well polynomials of degree  <MathJax inline>{"\\( k \\)"}</MathJax>, can approximate <MathJax inline>{"\\( A^{-1}\\)"}</MathJax>, over the spectrum of <MathJax inline>{"\\( A \\)"}</MathJax>.
        </li>
      </ul>


      <h2 className="text-sm font-semibold md:text-base">4. Preconditioning</h2>
      <p>
        Replace the system by a better-conditioned one. In “left” form, solve <MathJax inline>{"\\( M^{-1}Ax = M^{-1}b \\)"}</MathJax>; in “right” form,
        solve <MathJax inline>{"\\( A M^{-1} y = b \\)"}</MathJax> then <MathJax inline>{"\\( x=M^{-1}y \\)"}</MathJax>.
        Good <MathJax inline>{"\\( M \\)"}</MathJax> is cheap to apply and captures key structure. In this workspace:
      </p>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Diagonal / (Damped) Jacobi:</strong> zero setup, fully parallel.</li>
        <li><strong>GS / SSOR:</strong> inexpensive triangular solves; SSOR preserves symmetry (useful with CG).</li>
      </ul>

      <h2 className="text-sm font-semibold md:text-base">5. Stopping Criteria and Diagnostics</h2>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Residual:</strong> stop when <MathJax inline>{"\\( \\|r^{(k)}\\|_2 \\le \\varepsilon_{\\text{abs}} + \\varepsilon_{\\text{rel}}\\,\\|b\\|_2 \\)"}</MathJax>.</li>
        <li><strong>Step size:</strong> <MathJax inline>{"\\( \\|x^{(k+1)}-x^{(k)}\\| \\)"} </MathJax> small relative to <MathJax inline>{"\\( \\|x^{(k)}\\| \\)"}</MathJax>.</li>
      </ul>

      <h2 className="text-sm font-semibold md:text-base">6. Method Selection Guidelines</h2>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>SPD, well-conditioned:</strong> CG (with diagonal/SSOR preconditioning) is typically best-in-class.</li>
        <li><strong>Nonsymmetric or indefinite:</strong> GMRES (often restarted) with Jacobi/GS/SSOR preconditioning.</li>
        <li><strong>Very cheap smoothers or preconditioners needed:</strong> Richardson, (damped) Jacobi, GS/SSOR.</li>
        <li><strong>Ill-conditioning:</strong> rely on preconditioning; stationary methods alone will be slow.</li>
      </ul>

      <h2 className="text-sm font-semibold md:text-base">7. Short Analytical Snapshots</h2>
      <div className="space-y-3">
        <div>
          <p><strong>Jacobi/GS as Fixed-Point Maps.</strong></p>
          <MathJax>{String.raw`\[
          \begin{aligned}
            x^{(k+1)} - x^* &= G\,(x^{(k)} - x^*) ,\\
            \|e^{(k)}\| &\le \|G\|^k \|e^{(0)}\|,\\
            \rho(G)&<1 \Rightarrow e^{(k)}\!\to 0.
            \end{aligned}
          \]`}</MathJax>
          <p className="mt-1">Geometrically, each step multiplies the error by the linear map <MathJax inline>{"\\( G \\)"}</MathJax>; the smaller <MathJax inline>{"\\( \\rho(G) \\)"}</MathJax>, the faster the decay.</p>
        </div>
        <div>
          <p><strong>CG Finite-Termination (Ideal Arithmetic).</strong></p>
          <MathJax>{String.raw`\[
          \begin{aligned}
            x^{(n)} = x^* \quad&\text{if }\dim \mathcal{K}_n = n,\\
            &\text{and exact arithm.}
            \end{aligned}
          \]`}</MathJax>
          <p className="mt-1">In practice, roundoff and eigenvalue clustering govern the observed iteration count; preconditioning reshapes the spectrum to reduce the effective condition number.</p>
        </div>
        <div>
          <p><strong>GMRES Residual Minimization.</strong></p>
          <MathJax>{String.raw`\[
            \|r^{(k)}\|_2 = \min_{y\in\mathbb{R}^k}\ \bigl\| \beta e_1 - \underline{H}_k y \bigr\|_2,
          \]`}</MathJax>
          <p className="mt-1">Arnoldi builds an orthonormal basis; the small least-squares problem enforces a monotone decrease of <MathJax inline>{"\\( \\|r^{(k)}\\|_2 \\)"}</MathJax> until restart/roundoff.</p>
        </div>
        <div>
          <p><strong>Direction Choices, Intuitively.</strong></p>
          <p>Richardson follows the residual; Jacobi/GS correct along coordinate-coupled directions; CG follows <em>A-orthogonal</em> directions (optimal in the <MathJax inline>{"\\( A \\)"}</MathJax>-norm); GMRES picks the step that most decreases the 2-norm of the residual.</p>
        </div>
      </div>

      <h2 className="text-sm font-semibold md:text-base">8. Practical Hints for This Workspace</h2>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Initialization:</strong> zero vector by default; supplying a good <MathJax inline>{"\\( x^{(0)} \\)"}</MathJax> can drastically reduce iterations.</li>
        <li><strong>Choosing <MathJax inline>{"\\( \\omega \\)"}</MathJax>:</strong> try <MathJax inline>{"\\( \\omega\\in[1,1.9] \\)"}</MathJax> for SOR/SSOR; for Jacobi
          start with <MathJax inline>{"\\( \\omega\\in[0.6,1.0] \\)"}</MathJax>. Compare semilog residual plots.</li>
      </ul>

      <h2 className="text-sm font-semibold md:text-base">9. Comparing Richardson and CG on a Simple Example</h2>
      <p>
        To see why Krylov methods such as Conjugate Gradient (CG) typically outperform basic stationary schemes,
        let’s compare them on the same simple setting. Suppose <MathJax inline>{"\\( A \\)"}</MathJax> is symmetric positive definite (SPD) with eigenvalues
        in <MathJax inline>{"\\( [\\lambda_{\\min},\\lambda_{\\max}] \\)"}</MathJax>.
        Both Richardson and CG can be interpreted as applying polynomials 
        in <MathJax inline>{"\\( A \\)"}</MathJax> to the initial error <MathJax inline>{"\\( e^{(0)} = x^*-x^{(0)} \\)"}</MathJax>:
      </p>

      <MathJax>{String.raw`\[
  e^{(k)} = p_k(A)e^{(0)}, \qquad p_k(0)=1.
\]`}</MathJax>

      <p>
        The difference is <em>how</em> these polynomials are chosen.
        Richardson fixes a single damping factor <MathJax inline>{"\\( \\alpha \\)"}</MathJax> and repeatedly applies
        the same linear map <MathJax inline>{"\\( I - \\alpha A \\)"}</MathJax>,
        so convergence speed depends directly on how tightly clustered the eigenvalues of <MathJax inline>{"\\( A \\)"}</MathJax> are.
      </p>

      <p>
        Choosing the optimal constant step size <MathJax inline>{"\\( \\alpha_* = 2 / (\\lambda_{\\min} + \\lambda_{\\max}) \\)"}</MathJax> minimizes the 
        worst-case amplification factor over the spectrum, yielding
      </p>

      <MathJax>{String.raw`\[
  \|e^{(k)}\|_2 \le
  \left(\frac{\kappa-1}{\kappa+1}\right)^{\!k}
  \|e^{(0)}\|_2,
  \,
  \kappa = \frac{\lambda_{\max}}{\lambda_{\min}}.
\]`}</MathJax>

      <p>
        CG, by contrast, builds <MathJax inline>{"\\( p_k \\)"}</MathJax> adaptively to 
        minimize the <MathJax inline>{"\\( A \\)"}</MathJax>-norm of the error over the entire Krylov subspace.
        Effectively, CG finds the polynomial of degree <MathJax inline>{"\\( k \\)"}</MathJax> that best damps all 
        eigencomponents simultaneously—rather than using the same factor for every one.
      </p>

      <MathJax>{String.raw`\[
  \|e^{(k)}\|_A \le
  2\!\left(\frac{\sqrt{\kappa}-1}{\sqrt{\kappa}+1}\right)^{\!k}
  \|e^{(0)}\|_A.
\]`}</MathJax>

      <p>
        This bound shows that CG’s convergence depends on the square root of the condition number,
        not the condition number itself—an enormous advantage for ill-conditioned but SPD systems.
        Intuitively, each CG step uses information from all previous residuals to “cancel out”
        the slowest modes of error that plague stationary iterations.
      </p>

      <p>
        In practice, a good preconditioner reduces <MathJax inline>{"\\( \\kappa(A) \\)"}</MathJax> and transforms the spectrum to be tighter,
        so CG can converge in just a handful of iterations even for very large problems.
      </p>

      <h2 className="text-sm font-semibold md:text-base">11. One-Step Example (All methods)</h2>
      <p>
        Consider <MathJax inline>{"\\( A=\\begin{bmatrix}4 & 1\\\\ 1 & 3\\end{bmatrix},\\quad b=\\begin{bmatrix}1\\\\2\\end{bmatrix},\\quad x^{(0)}=\\begin{bmatrix}0\\\\0\\end{bmatrix} \\)"}</MathJax>.
        We perform <em>one</em> iteration for each method. We follow the same splitting convention used above:
        <MathJax inline>{"\\( A=D-L-U \\)"}</MathJax> with <em>\\(L,U\\) storing the negatives of the strictly lower/upper parts</em>.
      </p>

      <div className="space-y-2">
        <p><strong>Richardson</strong> (take <MathJax inline>{"\\( \\alpha=0.25 \\)"}</MathJax>):</p>
        <MathJax>{String.raw`\[
        \begin{aligned}
          x^{(1)} &= x^{(0)} + \alpha\,(b-Ax^{(0)})\\
           &= 0 + 0.25\,b
          = \begin{bmatrix}0.25\\0.50\end{bmatrix}.
        \end{aligned}
        \]`}</MathJax>

        <p><strong>Jacobi:</strong></p>
        <MathJax>{String.raw`\[
          x^{(1)} = D^{-1} b
          = \begin{bmatrix}1/4\\ 2/3\end{bmatrix}
          \approx \begin{bmatrix}0.25\\0.667\end{bmatrix}.
        \]`}</MathJax>

        <p><strong>Gauss–Seidel (forward):</strong></p>
        <MathJax>{String.raw`\[
        \begin{aligned}
          (D-L)x^{(1)} &= b + U x^{(0)} = b\\
          &\;\Rightarrow\;
          x^{(1)} = \begin{bmatrix}0.25\\ 0.583\end{bmatrix}.
          \end{aligned}
        \]`}</MathJax>

        <p><strong>Gauss–Seidel (backward):</strong></p>
        <MathJax>{String.raw`\[
        \begin{aligned}
          (D-U)x^{(1)} &= b + L x^{(0)} = b\\
          &\;\Rightarrow\;
          x^{(1)} = \begin{bmatrix}0.0833\\ 0.667\end{bmatrix}.
          \end{aligned}
        \]`}</MathJax>

        <p><strong>Symmetric Gauss–Seidel (forward then backward in one step):</strong></p>
        <MathJax>{String.raw`\[
        \begin{aligned}
          (D-L)x^{(1/2)} &= b,\\
          (D-U)x^{(1)} &= b + L x^{(1/2)}\\
          \;\Rightarrow\;
          x^{(1)} \approx \begin{bmatrix}0.104\\ 0.583\end{bmatrix}.
          \end{aligned}
        \]`}</MathJax>

        <p><strong>SSOR</strong> (take <MathJax inline>{"\\( \\omega=1.2 \\)"}</MathJax>):</p>
        <MathJax>{String.raw`\[
        \begin{aligned}
          (D-\omega L)x^{(1/2)} &= \omega b,\\
          (D-\omega U)x^{(1)} &= \omega b \\
          &+ \bigl[(1-\omega)D+\omega L\bigr]x^{(1/2)}\\
          \Rightarrow
          &x^{(1)} \approx \begin{bmatrix}0.0768\\ 0.544\end{bmatrix}.
          \end{aligned}
        \]`}</MathJax>

        <p><strong>Conjugate Gradient (1 step):</strong> with <MathJax inline>{"\\( p_0=r_0=b \\)"}</MathJax>,</p>
        <MathJax>{String.raw`\[
        \begin{aligned}
          \alpha_{\mathrm{CG}}&=\frac{r_0^\top r_0}{p_0^\top A p_0}=\frac{5}{20}=0.25,\\
          x^{(1)}&=x^{(0)}+\alpha_{\mathrm{CG}} r_0=\begin{bmatrix}0.25\\0.50\end{bmatrix}.
          \end{aligned}
        \]`}</MathJax>

        <p><strong>GMRES(1):</strong> minimize <MathJax inline>{"\\( \\|r_0-\\alpha A r_0\\|_2 \\)"}</MathJax> with <MathJax inline>{"\\( r_0=b \\)"}</MathJax>, giving</p>
        <MathJax>{String.raw`\[
        \begin{aligned}
          \alpha_{\mathrm{GMRES(1)}}&=\frac{r_0^\top A r_0}{\|A r_0\|_2^2}
          =\frac{20}{85}\approx 0.2353,\\
          x^{(1)}&=\alpha r_0=\begin{bmatrix}0.2353\\ 0.4706\end{bmatrix}.
          \end{aligned}
        \]`}</MathJax>

        <p className="mt-2">
          (Exact solution for reference:
          <MathJax inline>{"\\( x^*=A^{-1}b=\\tfrac{1}{11}\\begin{bmatrix}1\\\\7\\end{bmatrix}\\approx(0.0909,\\,0.6364) \\)"}</MathJax>.)
        </p>
      </div>
    </div>
  );
}
